import { Environment, getCountryCodeBookId, getDefaultAppLanguage, Market } from '@leanpay/utils'

import Iphone8Img from 'assets/images/iphone_8.png'
import Iphone11Img from 'assets/images/iphone_11.png'
import Iphone14Img from 'assets/images/iphone_14.png'

// START COMMON CONSTANTS
const getMarketFromHostname = (): Market => {
    const hostname = window.location.hostname

    if (hostname.includes('.leanpay.ro') || hostname.includes('rou')) {
        return Market.ROM
    }

    if (hostname.includes('.leanpay.si') || hostname.includes('slo')) {
        return Market.SLO
    }

    if (hostname.includes('.leanpay.hu') || hostname.includes('hun')) {
        return Market.HUN
    }

    throw new Error(`[MARKET] Unknown hostname: ${hostname}`)
}
const isPrivateIp =
    /^(10\.\d+\.\d+\.\d+|192\.168\.\d+\.\d+|172\.(1[6-9]|2\d|3[01])\.\d+\.\d+)$/.test(
        window.location.hostname,
    )
const isRunningOnLocalhost =
    window.location.hostname.includes('localhost') ||
    window.location.hostname.includes('127.0.0.1') ||
    isPrivateIp ||
    (window.location.hostname.includes('leanpay') && !window.location.hostname.includes('.'))

export const currentMarket = isRunningOnLocalhost
    ? (import.meta.env.VITE_MARKET as Market)
    : getMarketFromHostname()

export const isMarketSlo = currentMarket === Market.SLO
export const isMarketRom = currentMarket === Market.ROM
export const isMarketHun = currentMarket === Market.HUN

const getGtmId = () => {
    if (isMarketSlo) {
        return 'GTM-WKKBF5KC'
    }
    if (isMarketRom) {
        return 'GTM-NPG78W56'
    }
    if (isMarketHun) {
        return 'GTM-TK88QW62'
    }

    return ''
}

export const gtmId = getGtmId()

const getEnvironmentFromHostname = () => {
    const hostname = window.location.hostname

    if (isRunningOnLocalhost) {
        return Environment.LOCAL
    }

    if (hostname.includes('dev')) {
        return Environment.TILT
    }

    if (hostname.includes('test-self-checkout')) {
        return Environment.TEST
    }
    if (hostname.includes('stage-self-checkout')) {
        return Environment.STAGE
    }

    if (hostname.includes('self-checkout')) {
        return Environment.PROD
    }

    throw new Error(`[ENV] Unknown hostname: ${hostname}`)
}

export const currentEnvironment = getEnvironmentFromHostname()

export const isLocalEnvironment = currentEnvironment === Environment.LOCAL
export const isTestEnvironment = currentEnvironment === Environment.TEST
export const isStageEnvironment = currentEnvironment === Environment.STAGE
export const isProdEnvironment = currentEnvironment === Environment.PROD
export const isNonProdLiveEnvironment = !isProdEnvironment && !isLocalEnvironment

export const defaultAppLanguage = getDefaultAppLanguage(currentMarket)
export const countryCodebookId = getCountryCodeBookId(currentMarket)

// END COMMON CONSTANTS

export enum CacheKey {
    LoanAmountLimits = 'LoanAmountLimits',
    Nationality = 'Nationality',
    FinancialData = 'FinancialData',
    Settlement = 'Settlement',
    PaymentData = 'PaymentData',
    DownloadInitialOffer = 'DownloadInitialOffer',
    SystemConfig = 'SystemConfig',
    PEPQuestionnaire = 'PEPQuestionnaire',
    SessionInfo = 'SessionInfo',
    GlobalDocument = 'GlobalDocument',
    FinancialDetails = 'FinancialDetails',
    AddressDetails = 'AddressDetails',
    AuthorizationCode = 'AuthorizationCode',
    AuthorizationCodeStatus = 'AuthorizationCodeStatus',
    PreQualifyVendorInit = 'PreQualifyVendorInit',
    PreQualifiedInfo = 'PreQualifiedInfo',
}

export enum SelfCheckoutRoute {
    SplashScreen = 'splash-screen',
    Intro = 'intro',

    Onboarding = 'onboarding',
    OnboardingShort = 'onboarding-short',

    PreQualifyIntro = 'pre-qualify-intro',
    RequestOtp = 'request-otp',
    Login = 'login',
    PersonalDetails = 'personal-details',
    TwoFaPin = 'two-fa-pin',
    AddressDetails = 'address-details',
    ValidateOfferPQ = 'validate-offer-pre-qualify',
    PreQualifySuccess = 'pre-qualify-success',
    SetPurchaseAmount = 'set-purchase-amount',
    ChoseInstallmentPlan = 'choose-installment',
    ValidateOfferCheckout = 'validate-offer',

    SigContract = 'sign-contract',
    ForgotTwoFaPin = 'forgot-two-fa-pin',
    PepQuestionnaire = 'pep-questionnaire',

    VerificationInfo = 'verification-info',
    KYCVerification = 'kyc-verification',
    KYCVerificationSuccess = 'kyc-verification-success',
    Payment = 'payment',
    PaymentStatus = 'payment-status',

    CheckoutError = 'checkout-error',
    PrequalifyError = 'prequalified-error',
    CheckoutSuccess = 'checkout-success',
}

export const cartItems = [
    {
        name: 'Vendor product 01',
        sku: '1001',
        price: 10.0,
        qty: 1,
        lpProdCode: 'product1',
    },
    {
        name: 'Vendor product 02',
        sku: '1002',
        price: 20.0,
        qty: 2,
        lpProdCode: 'product1',
    },
    {
        name: 'Vendor product 03',
        sku: '1003',
        price: 30.0,
        qty: 3,
        lpProdCode: 'product1',
    },
]

export const mockProductList = [
    {
        description: `   EMundia je trgovina za dom, specializirana za sisteme za izboljšanje kvalitete
        bivanja, kot so: sistemi za ogrevanje, hlajenje, prezračevanje, čistilne naprave
        in zbiralniki vode. V naši ponudbi pa najdete tudi belo tehniko, izdelke za vrt,
        orodje in mnogo drugih izdelkov za izboljšanje vsakdanjega življenja.`,
        name: 'Apple iPhone 14 - EMundia d.o.o.',
        productImage: Iphone14Img,
        vendorApiKey: '5182483f-9e19-407c-9cfd-2b62ae0d3583',
        vendorTransactionId: `test-ignore-${new Date().getTime()}`,
        successUrl: '/vendor-mock#success',
        errorUrl: '/vendor-mock#error',
        vendorFirstName: 'Janez',
        vendorLastName: 'Novak',
        vendorEmail: 'janez.novak@leanpay.com',
        language: 'en',
        // vendorProductCode: 'emundiaVendorProductCode',
        amount: 900,
        cartItems: cartItems,
    },
    {
        description: 'Lorem Ipsum is simply dummy text of the printing and typesetting industry.',
        name: 'Apple iPhone 11',
        productImage: Iphone11Img,
        vendorApiKey: '4',
        vendorTransactionId: `test-ignore-${new Date().getTime()}`,
        successUrl: '/vendor-mock#success',
        errorUrl: '/vendor-mock#error',
        vendorFirstName: 'Janez',
        vendorLastName: 'Novak',
        vendorEmail: 'janez.novak@leanpay.com',
        language: 'en',
        //  vendorProductCode: '',
        amount: 150,
        cartItems: cartItems,
    },
    {
        description: 'Lorem Ipsum is simply dummy text of the printing and typesetting industry.',
        name: 'Apple iPhone 8',
        productImage: Iphone8Img,
        vendorApiKey: '4',
        vendorTransactionId: `test-ignore-${new Date().getTime()}`,
        successUrl: '/vendor-mock#success',
        errorUrl: '/vendor-mock#error',
        vendorFirstName: 'Janez',
        vendorLastName: 'Novak',
        vendorEmail: 'janez.novak@leanpay.com',
        language: 'en',
        //  vendorProductCode: '',
        amount: 150,
        cartItems: cartItems,
    },
]
export const mockProductListHUN = [
    {
        description: `EMundia je trgovina za dom, specializirana za sisteme za izboljšanje kvalitete
        bivanja, kot so: sistemi za ogrevanje, hlajenje, prezračevanje, čistilne naprave
        in zbiralniki vode. V naši ponudbi pa najdete tudi belo tehniko, izdelke za vrt,
        orodje in mnogo drugih izdelkov za izboljšanje vsakdanjega življenja.`,
        name: 'Apple iPhone 14 - EMundia d.o.o.',
        productImage: Iphone14Img,
        vendorApiKey: '4',
        vendorTransactionId: `test-ignore-${new Date().getTime()}`,
        successUrl: '/vendor-mock',
        errorUrl: '/vendor-mock',
        vendorFirstName: 'Laszlo',
        vendorLastName: 'Varga',
        vendorEmail: 'laszlo.varga@leanpay.com',
        language: 'en',
        // vendorProductCode: 'emundiaVendorProductCode',
        amount: 30000,
        cartItems: cartItems,
    },
    {
        description: 'Lorem Ipsum is simply dummy text of the printing and typesetting industry.',
        name: 'Apple iPhone 11',
        productImage: Iphone11Img,
        vendorApiKey: '4',
        vendorTransactionId: `test-ignore-${new Date().getTime()}`,
        successUrl: '/vendor-mock',
        errorUrl: '/vendor-mock',
        vendorFirstName: 'Laszlo',
        vendorLastName: 'Varga',
        vendorEmail: 'laszlo.varga@leanpay.com',
        language: 'en',
        //  vendorProductCode: '',
        amount: 270000,
        cartItems: cartItems,
    },
    {
        description: 'Lorem Ipsum is simply dummy text of the printing and typesetting industry.',
        name: 'Apple iPhone 8',
        productImage: Iphone8Img,
        vendorApiKey: '4',
        vendorTransactionId: `test-ignore-${new Date().getTime()}`,
        successUrl: '/vendor-mock',
        errorUrl: '/vendor-mock',
        vendorFirstName: 'Laszlo',
        vendorLastName: 'Varga',
        vendorEmail: 'laszlo.varga@leanpay.com',
        language: 'en',
        //  vendorProductCode: '',
        amount: 28000,
        cartItems: cartItems,
    },
]
export const mockProductListROM = [
    {
        description: `EMundia je trgovina za dom, specializirana za sisteme za izboljšanje kvalitete
        bivanja, kot so: sistemi za ogrevanje, hlajenje, prezračevanje, čistilne naprave
        in zbiralniki vode. V naši ponudbi pa najdete tudi belo tehniko, izdelke za vrt,
        orodje in mnogo drugih izdelkov za izboljšanje vsakdanjega življenja.`,
        name: 'Apple iPhone 14 - EMundia d.o.o.',
        productImage: Iphone14Img,
        vendorApiKey: '4',
        vendorTransactionId: `test-ignore-${new Date().getTime()}`,
        successUrl: '/vendor-mock',
        errorUrl: '/vendor-mock',
        vendorFirstName: 'Adrian',
        vendorLastName: 'Mutu',
        vendorEmail: 'adrian.mutu@leanpay.com',
        language: 'en',
        // vendorProductCode: 'emundiaVendorProductCode',
        amount: 900,
        cartItems: cartItems,
    },
    {
        description: 'Lorem Ipsum is simply dummy text of the printing and typesetting industry.',
        name: 'Apple iPhone 11',
        productImage: Iphone11Img,
        vendorApiKey: '4',
        vendorTransactionId: `test-ignore-${new Date().getTime()}`,
        successUrl: '/vendor-mock',
        errorUrl: '/vendor-mock',
        vendorFirstName: 'Adrian',
        vendorLastName: 'Mutu',
        vendorEmail: 'adrian.mutu@leanpay.com',
        language: 'en',
        //  vendorProductCode: '',
        amount: 150,
        cartItems: cartItems,
    },
    {
        description: 'Lorem Ipsum is simply dummy text of the printing and typesetting industry.',
        name: 'Apple iPhone 8',
        productImage: Iphone8Img,
        vendorApiKey: '4',
        vendorTransactionId: `test-ignore-${new Date().getTime()}`,
        successUrl: '/vendor-mock',
        errorUrl: '/vendor-mock',
        vendorFirstName: 'Adrian',
        vendorLastName: 'Mutu',
        vendorEmail: 'adrian.mutu@leanpay.com',
        language: 'en',
        //  vendorProductCode: '',
        amount: 250,
        cartItems: cartItems,
    },
]

export const getDashboardUrl = (): string => {
    switch (currentMarket) {
        case Market.HUN:
            if (isProdEnvironment) {
                return 'https://app.leanpay.hu/dashboard'
            }

            if (isTestEnvironment) {
                return 'https://test-app.leanpay.hu/dashboard'
            }

            if (isStageEnvironment) {
                return 'https://stage-app.leanpay.hu/dashboard'
            }
            return 'http://localhost:4200/dashboard'
        case Market.ROM:
            if (isProdEnvironment) {
                return 'https://app.leanpay.ro/dashboard'
            }

            if (isTestEnvironment) {
                return 'https://test-app.leanpay.ro/dashboard'
            }

            if (isStageEnvironment) {
                return 'https://stage-app.leanpay.ro/dashboard'
            }

            return 'http://localhost:4200/dashboard'
        case Market.SLO:
            if (isProdEnvironment) {
                return 'https://app.leanpay.si/dashboard'
            }

            if (isTestEnvironment) {
                return 'https://test-app.leanpay.si/dashboard'
            }

            if (isStageEnvironment) {
                return 'https://stage-app.leanpay.si/dashboard'
            }
            return 'http://localhost:4200/dashboard'
        default:
            return 'http://localhost:4200/dashboard'
    }
}

export const getPQUrl = (): string => {
    switch (currentMarket) {
        case Market.SLO:
            if (isProdEnvironment) {
                return 'https://pre-qualify.leanpay.si/?vendor=3ER7JP'
            }
            if (isTestEnvironment) {
                return 'https://test-pre-qualify.leanpay.si/?vendor=3ER7JP'
            }
            if (isStageEnvironment) {
                return 'https://stage-pre-qualify.leanpay.si/?vendor=3ER7JP'
            }
            return 'http://localhost:4303/?vendor=3ER7JP'

        case Market.ROM:
            if (isProdEnvironment) {
                return 'https://pre-qualify.leanpay.ro/?vendor=3ER7JP'
            }
            if (isTestEnvironment) {
                return 'https://test-pre-qualify.leanpay.ro/?vendor=3ER7JP'
            }
            if (isStageEnvironment) {
                return 'https://stage-pre-qualify.leanpay.ro/?vendor=3ER7JP'
            }
            return 'http://localhost:4303/?vendor=3ER7JP'

        default:
            return 'http://localhost:4303/?vendor=3ER7JP'
    }
}

export enum OnboardingStep {
    OnboardingStep1 = 'OnboardingStep1',
    OnboardingStep2 = 'OnboardingStep2',
    OnboardingStep3 = 'OnboardingStep3',
    OnboardingStep4 = 'OnboardingStep4',
    OnboardingStep5 = 'OnboardingStep5',
    OnboardingStep6 = 'OnboardingStep6',
}
